/**
 * useEditorSelection — manages tool / step / preset selection state
 * for the EditorPage composition shell.
 */

import React from 'react';

import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useAppStore } from '@/store/useAppStore';
import { useEditorStore } from '@/store/useEditorStore';
import { usePreferencesStore } from '@/store/usePreferencesStore';
import { usePresetStore } from '@/store/usePresetStore';

export function useEditorSelection() {
  const [selectedToolId, setSelectedToolId] = useLocalStorage<string | null>('app.selectedToolId', null);
  const [selectedPresetId, setSelectedPresetId] = useLocalStorage<string | null>('app.selectedPresetId', null);

  const selectedStepId = useEditorStore((s) => s.selectedStepId);
  const setSelectedStepId = useEditorStore((s) => s.setSelectedStepId);
  const setActiveToolId = useEditorStore((s) => s.setActiveToolId);
  const [pipelineSettingsOpen, setPipelineSettingsOpen] = React.useState(false);

  const { addTool, addToolWithConfig, run } = useAppStore();
  const autoRunPipeline = usePreferencesStore((s) => s.autoRunPipeline);

  const selectedPreset = usePresetStore(
    React.useCallback(
      (s) => (selectedPresetId ? s.presets.find((p) => p.id === selectedPresetId) ?? null : null),
      [selectedPresetId]
    )
  );

  // Keep selectedPresetId in sync — clear if preset no longer matches tool
  React.useEffect(() => {
    if (selectedPresetId && (!selectedPreset || selectedPreset.toolId !== selectedToolId)) {
      setSelectedPresetId(null);
    }
  }, [selectedPreset, selectedPresetId, selectedToolId, setSelectedPresetId]);

  /* ---------- callbacks ---------- */

  const resetStepSelection = React.useCallback(() => {
    setSelectedStepId(null);
    setPipelineSettingsOpen(false);
  }, []);

  const clearToolSelection = React.useCallback(() => {
    setSelectedToolId(null);
    setActiveToolId(null);
  }, [setActiveToolId, setSelectedToolId]);

  const handleAddTool = React.useCallback(
    (toolId: string) => {
      addTool(toolId);
      setSelectedPresetId(null);
      setSelectedToolId(toolId);
      setSelectedStepId(null);
      setActiveToolId(toolId);

      if (autoRunPipeline) {
        setTimeout(() => run(), 0);
      }
    },
    [addTool, autoRunPipeline, run, setActiveToolId, setSelectedPresetId, setSelectedStepId, setSelectedToolId]
  );

  const handleAddPresetToPipeline = React.useCallback(
    (presetId: string, toolId: string, config: Record<string, unknown>, name: string) => {
      addToolWithConfig(toolId, config, name);
      setSelectedPresetId(presetId);
      setSelectedToolId(toolId);
      setSelectedStepId(null);
      setActiveToolId(toolId);

      if (autoRunPipeline) {
        setTimeout(() => run(), 0);
      }
    },
    [addToolWithConfig, autoRunPipeline, run, setActiveToolId, setSelectedPresetId, setSelectedStepId, setSelectedToolId]
  );

  const handleSelectStep = React.useCallback((stepId: string) => {
    setPipelineSettingsOpen(false);
    setSelectedStepId(stepId);
  }, []);

  const handleOpenPipelineSettings = React.useCallback(() => {
    setSelectedStepId(null);
    setPipelineSettingsOpen(true);
  }, []);

  const closePipelineSettings = React.useCallback(() => {
    setPipelineSettingsOpen(false);
  }, []);

  const handleSelectPreset = React.useCallback(
    (presetId: string, toolId: string) => {
      setSelectedPresetId(presetId);
      setSelectedToolId(toolId);
      setSelectedStepId(null);
      setActiveToolId(toolId);
    },
    [setActiveToolId, setSelectedPresetId, setSelectedToolId]
  );

  return {
    selectedToolId,
    selectedPresetId,
    selectedStepId,
    pipelineSettingsOpen,
    resetStepSelection,
    clearToolSelection,
    handleAddTool,
    handleAddPresetToPipeline,
    handleSelectStep,
    handleOpenPipelineSettings,
    closePipelineSettings,
    handleSelectPreset,
  };
}
